/**
 * The typed surface of `apps/server`, over the transport in `client.ts`. Components never
 * call this directly — reads and writes go through `src/data/`, which owns offline behavior.
 */

import type { ImportBody } from '@gymcloud/macrofactor'

import { ApiError, type ApiClient } from '~/api/client'
import type {
  Enrollment,
  ExploreQuery,
  LibraryListing,
  LibraryTemplate,
  TargetJson,
  TemplateVersion,
  WorkoutTemplate,
} from '~/data/templates'
import type {
  AccountSettings,
  DistanceUnits,
  Exercise,
  Feedback,
  FeedbackStatus,
  LoggedSet,
  SetInput,
  Units,
  User,
  Workout,
} from '~/data/types'
import { updatedAtOf, type SyncPage, type WorkoutRow } from '~/data/sync'
import { appContext } from '~/lib/app-context'
import type { PacketPhotos } from '~/lib/packet-tray'
import type { WeighIn, WeighInPhoto, Measurement, PhotoType } from '~/data/weighins'
import type {
  Amount,
  Entry,
  Food,
  FoundFood,
  CalorieFloorChoice,
  CalorieLevel,
  DayTargets,
  Diet,
  Goal,
  GoalMode,
  Aim,
  ProteinIntake,
  Nutrients,
  Portion,
  Reference,
  Meal,
  MealLog,
  MealSlot,
  MealState,
} from '~/data/nutrition'

/** What saving a food sends: everything but what the server works out for a recipe. */
export interface PutFoodBody {
  readonly name: string
  readonly emoji?: string | null
  readonly brand?: string | null
  readonly barcode?: string | null
  readonly reference: Reference
  readonly nutrients: Partial<Nutrients> & { calories: number }
  readonly portions: readonly Portion[]
  /** Grams per millilitre, where the food is a liquid — typed on the form, or carried over
   * from a liquid this is a copy of. */
  readonly density?: number | null
  readonly recipe?: {
    readonly servings: number
    /** What the whole weighs in grams, where whoever wrote it said. */
    readonly totalGrams?: number | null
    readonly ingredients: readonly {
      readonly name: string
      readonly food: Food | null
      readonly amount: Amount
      readonly nutrients: Nutrients
    }[]
  }
}

export interface PutEntryBody {
  readonly day: string
  readonly time: string
  readonly name?: string
  readonly food: Food | null
  readonly amount: Amount
  /** A quick add's own numbers; a food's are worked out from the amount. */
  readonly nutrients?: Partial<Nutrients> & { calories: number }
  /** Logged through a meal: the foods-logged-before list skips it. */
  readonly fromMeal?: boolean
  /** Which meal it was logged from, where the account keeps that meal. */
  readonly mealId?: string | null
  /**
   * The moment the device first wrote it, which a day orders the entries of one minute by. The
   * server keeps it for a new row rather than stamping its own, so the entries of one press come
   * back in the order they were written.
   */
  readonly createdAt?: number
}

/** What saving a meal sends: the whole of it, as its page stands. */
export interface PutMealBody {
  readonly name: string
  readonly emoji?: string | null
  readonly slots: readonly MealSlot[]
  readonly state?: MealState
  /** The logs the meal adopts: those it made before it was kept — the nutrition spec, Meals. */
  readonly adopt?: readonly string[]
}

/** A log of a meal as it is sent: the meal as it stood, where each slot stood, and when. */
export interface MealLogBody {
  readonly id: string
  readonly loggedAt: number
  readonly name: string
  readonly emoji: string | null
  readonly slots: readonly MealSlot[]
  readonly state: MealState
}

/** The log of something logged without being kept, as it is sent. The id travels in the path. */
export interface UnkeptLogBody {
  readonly name: string
  readonly emoji: string | null
  readonly slots: readonly MealSlot[]
  readonly state: MealState
  readonly loggedAt: number
}

/** The account's meals, and the logs of what was logged without being kept. */
export interface MealsAnswer {
  readonly meals: readonly Meal[]
  readonly unsaved: readonly MealLog[]
}

/** One entry the AI made of a meal — see the nutrition spec, Asking the AI. */
export interface AiEntry {
  readonly name: string
  readonly emoji: string
  readonly amount: Amount
  readonly nutrients: Nutrients
}

export interface AskAiBody {
  /** A JPEG, base64 without a `data:` prefix. */
  readonly image: string | null
  readonly description: string | null
  /** The answer as it stands, when asking again. */
  readonly previous: readonly AiEntry[] | null
  /** The follow-up, when asking again. */
  readonly details: string | null
}

export interface AiAnswer {
  readonly entries: AiEntry[]
  /** What the AI would call the meal the entries make, and the mark it wears. */
  readonly name: string | null
  readonly emoji: string | null
  /** The model's short remark — why it found nothing, say. */
  readonly note: string | null
}

/**
 * What the AI read off a packet — the nutrition spec, Own foods: a draft of a food rather than
 * a food, for the form to open holding and somebody to look over before it is saved.
 */
export interface AiLabel {
  readonly name: string
  readonly brand: string | null
  readonly barcode: string | null
  readonly emoji: string
  readonly reference: '100g' | '100ml'
  readonly nutrients: Nutrients
  readonly servingName: string | null
  readonly servingAmount: number | null
}

export interface PutGoalBody {
  readonly effectiveFrom?: string
  readonly mode: GoalMode
  readonly aim?: Aim | null
  readonly targetWeightKg?: number | null
  readonly rateKgPerWeek?: number | null
  readonly calories: number
  readonly protein: number
  readonly carbs: number
  readonly fat: number
  readonly weeklyCheckinWeekday: number
  readonly expenditureAtSet?: number | null
  readonly diet?: Diet | null
  readonly calorieFloor?: CalorieFloorChoice | null
  readonly proteinIntake?: ProteinIntake | null
  readonly calorieDistribution?: readonly CalorieLevel[] | null
  readonly dayTargets?: readonly DayTargets[] | null
}

/** New targets for the goal in force, from a day on — what a weekly check-in taken sends. */
export interface AddGoalTargetsBody {
  readonly effectiveFrom?: string
  readonly calories: number
  readonly protein: number
  readonly carbs: number
  readonly fat: number
  readonly expenditureAtSet?: number | null
  readonly dayTargets?: readonly DayTargets[] | null
}

export interface ListSetsParams {
  readonly limit?: number
  readonly before?: string
  readonly exercise?: string
  /** Exactly this exercise, by folded name — not the name-contains search `exercise` runs. */
  readonly exerciseName?: string
  readonly filmed?: boolean
  /** `false` leaves warm-up sets out; anything else leaves them in. */
  readonly warmups?: boolean
  readonly year?: number
}

export interface ListWorkoutsParams {
  /** Only the workouts this exercise was done in, by folded name. */
  readonly exerciseName?: string
}

export interface SetsPage {
  readonly sets: LoggedSet[]
  readonly nextCursor: string | null
}

export interface PutSetBody extends SetInput {
  readonly workoutId?: string | null
}

export interface PutWorkoutBody {
  readonly startedAt: number
  readonly finishedAt?: number | null
  readonly name?: string | null
  readonly mood?: string | null
  readonly openingNotes?: string | null
  readonly closingNotes?: string | null
  /** Which run it was played under, and which workout of that template it was. */
  readonly enrollmentId?: string | null
  readonly templateWorkout?: string | null
  /**
   * Every set the template planned, in order, logged or not. Without the ones passed over a
   * progression cannot tell a failed session from a shorter one.
   */
  readonly plan?: PlannedSetBody[]
}

export interface PlannedSetBody {
  readonly exerciseName: string
  readonly isWarmup: boolean
  readonly target: TargetJson | null
  readonly setId: string | null
  readonly logged: boolean
  /** Asked for by the assistant rather than planned — see `PlanEntry.byAssistant`. */
  readonly byAssistant?: boolean
}

export type MediaSlot = 'video' | 'thumbnail' | 'face-map'

export const EXPORT_GROUPS = ['training', 'body', 'nutrition', 'account'] as const
export type ExportGroup = (typeof EXPORT_GROUPS)[number]
export const EXPORT_FORMATS = ['json', 'csv'] as const
export type ExportFormat = (typeof EXPORT_FORMATS)[number]

/** What an import wrote, piece by piece, and which body stats it filled in. */
export interface ImportWritten {
  readonly entries: number
  readonly foods: number
  readonly favourites: number
  readonly goals: number
  readonly weighIns: number
  readonly metrics: number
  readonly profile: readonly string[]
}

export type { ImportBody }

/**
 * A search's answer: the foods found, and whether the library was among the answerers. The
 * account's own foods and the public ones still answer while the library cannot be reached,
 * and a caller handed only the foods would read the library's silence as it holding nothing.
 */
export interface FoodSearch {
  foods: FoundFood[]
  libraryUnavailable: boolean
}

/**
 * One pack the library is copied into, as the manifest names it — see
 * `/specs/features/food-sources.spec.md`, Packs. The web app downloads none; it reads the
 * manifest for what a search can be narrowed to.
 */
export interface PackListed {
  readonly id: string
  readonly name: string
  /** The short name, where the manifest was built with one. */
  readonly label?: string
  /** `openfoodfacts` or `usda`. */
  readonly source: string
  /** Where the products are sold, alpha-2 and lower case; empty on the generic pack. */
  readonly country: string
}

export interface Api {
  /**
   * The rows that have moved since a cursor, newest last, with a tombstone in the place of
   * one that is gone. The whole of what a client reads: every screen's list is worked out
   * from the mirror this fills.
   */
  sync: (params: { since: number; limit?: number }) => Promise<SyncPage>
  /** The chosen groups' pieces as one file, ready to be saved. */
  exportData: (groups: readonly ExportGroup[], format: ExportFormat) => Promise<Blob>
  /** One page of another app's export, read on this device into our own shapes. */
  importData: (body: ImportBody) => Promise<ImportWritten>
  signInWithGoogle: (idToken: string) => Promise<{ token: string; user: User }>
  signOut: () => Promise<void>
  getMe: () => Promise<User>
  updateUnits: (units: Units) => Promise<User>
  updateDistanceUnits: (units: DistanceUnits) => Promise<User>
  /** Whichever settings were sent; the rest are left as they stand. */
  updateSettings: (settings: Partial<AccountSettings>) => Promise<User>
  deleteAccount: () => Promise<void>
  /** Raises a 429 `ApiError` once the hour's worth has been sent. */
  sendFeedback: (body: string) => Promise<Feedback>
  /** Everything anybody has sent, newest first. Admins only. */
  listFeedback: () => Promise<Feedback[]>
  setFeedbackStatus: (id: string, status: FeedbackStatus) => Promise<Feedback>
  /** Upserts a day's numbers — a day has at most one weigh-in, so writing it again edits it. */
  putWeighIn: (
    day: string,
    body: { weightKg: number | null; measurements: Partial<Record<Measurement, number | null>> },
  ) => Promise<WeighIn>
  /** The photo's place on its day; the bytes follow through `uploadWeighInPhoto`. */
  putWeighInPhoto: (
    day: string,
    id: string,
    type: PhotoType,
    label: string | null,
  ) => Promise<WeighInPhoto>
  uploadWeighInPhoto: (id: string, media: Blob) => Promise<WeighInPhoto>
  deleteWeighInPhoto: (id: string) => Promise<void>
  /** Carries the token as a query parameter, like every URL an `<img>` fetches itself. */
  weighInPhotoUrl: (id: string) => string
  putEntry: (id: string, body: PutEntryBody) => Promise<Entry>
  deleteEntry: (id: string) => Promise<void>
  /** Several at once, in one request — what collapsing entries into a recipe does. */
  deleteEntries: (ids: readonly string[]) => Promise<void>
  setDayIncomplete: (day: string, incomplete: boolean) => Promise<void>
  /** Sets a goal from a day on; every set starts a new goal in the history. */
  putGoal: (body: PutGoalBody) => Promise<Goal>
  /**
   * Targets added to the goal in force, from a day on. It starts no goal: what a check-in
   * moves is what a day is asked for, and never what is being aimed at.
   */
  addGoalTargets: (body: AddGoalTargetsBody) => Promise<Goal>
  /** The weekday of the goal in force's weekly check-in, changed on the goal as it stands. */
  changeCheckinWeekday: (weeklyCheckinWeekday: number) => Promise<Goal>
  weeklyCheckedIn: (day: string) => Promise<void>
  /**
   * The goal in force stopped on a day, kept as one gone by. Not the same as deleting it: the
   * days already logged keep the targets that stood on them.
   */
  endGoal: (day: string) => Promise<Goal>
  /** The newest goal gone by back in force, destroying whatever stood over it. */
  reopenGoal: () => Promise<Goal>
  deleteGoal: () => Promise<void>
  /**
   * By name, across the library, the account's own foods and everyone's public ones — saying
   * whether the library answered, since a search it could not answer is not one that found
   * nothing.
   */
  searchFoods: (query: string, from?: readonly string[] | null) => Promise<FoodSearch>
  /** Every pack the library is copied into — what a search of it can be narrowed to. */
  packs: () => Promise<PackListed[]>
  /**
   * What the AI makes of a meal. Online only: an answer is worth nothing later, so this never
   * goes through the offline queue — a caller with no connection is told so and tries again.
   */
  askAi: (body: AskAiBody) => Promise<AiAnswer>
  /**
   * What the AI reads off a packet: the face of it, the table on the back, or both, and a
   * food's draft back. Refused with 503 where no AI is configured, 502 where it could not be
   * reached, and 422 where what was photographed is no food.
   */
  readLabel: (photos: PacketPhotos) => Promise<AiLabel>
  /** A food marked. Sending it again is the same favourite, not a second one. */
  addFavourite: (key: string, food: Food) => Promise<void>
  removeFavourite: (key: string) => Promise<void>
  /** The food carrying a barcode, or null where none does. */
  foodByBarcode: (code: string) => Promise<Food | null>
  /**
   * The foods a shared link refers to — the nutrition spec, Sharing. Answered in the order
   * asked, with null in the place of one that cannot be read, so the page opening a link can
   * say which member is missing rather than only that something is.
   */
  foodsByRef: (refs: readonly string[]) => Promise<(Food | null)[]>
  putFood: (id: string, body: PutFoodBody) => Promise<Food>
  deleteFood: (id: string) => Promise<void>
  putMeal: (id: string, body: PutMealBody) => Promise<Meal>
  deleteMeal: (id: string) => Promise<void>
  /** The meal was logged: a log of it as it stood, and the meal as it now stands. */
  logMeal: (mealId: string, body: MealLogBody) => Promise<Meal>
  /** Something logged without being kept, or logged again: the same log, moved to now. */
  putUnkeptLog: (id: string, body: UnkeptLogBody) => Promise<MealLog>
  /** A meal marked. Sending it again is the same favourite with a fresher copy. */
  addMealFavourite: (id: string, meal: Meal) => Promise<void>
  removeMealFavourite: (id: string) => Promise<void>
  /** The foods kept to hand, in the order they were kept. */
  putTemplate: (
    id: string,
    body: { name: string; script: string; forkedFromVersionId?: string | null },
  ) => Promise<WorkoutTemplate>
  deleteTemplate: (id: string) => Promise<void>
  /** Puts a template into Explore, offering the script as it stands. */
  publishTemplate: (id: string) => Promise<LibraryListing>
  /** Takes it out again. An admin may do this to anybody's. */
  unpublishTemplate: (id: string) => Promise<void>
  /** The script a run follows — the only way to plan a template you do not own. */
  getTemplateVersion: (id: string) => Promise<TemplateVersion>
  /** `versionId` starts exactly that version; `templateId` starts your own as it stands. */
  enroll: (body: {
    id: string
    templateId?: string
    versionId?: string
    startedAt: number
    anchorDay?: number | null
  }) => Promise<Enrollment>
  /** Moves a run on to another version of the same template, run and progressions intact. */
  moveEnrollmentToVersion: (id: string, versionId: string) => Promise<Enrollment>
  /** Moves which weekday a run's cycle begins on. Nothing else about the run moves. */
  setEnrollmentAnchor: (id: string, anchorDay: number | null) => Promise<Enrollment>
  endEnrollment: (id: string) => Promise<void>
  /** Removes a run outright — the half of restoring that never reaches the history. */
  deleteEnrollment: (id: string) => Promise<void>
  /** The published templates, narrowed by whatever the query names. */
  explore: (query: ExploreQuery) => Promise<LibraryListing[]>
  exploreTemplate: (templateId: string) => Promise<LibraryTemplate>
  listExercises: () => Promise<Exercise[]>
  /** One set by id, whatever window the timeline is holding. 404 when there is no such set. */
  getSet: (id: string) => Promise<LoggedSet>
  putSet: (id: string, body: PutSetBody) => Promise<LoggedSet>
  deleteSet: (id: string) => Promise<void>
  /** One workout with the sets in it. The plan it played rides as `plannedSets`. */
  getWorkout: (id: string) => Promise<{ workout: WorkoutRow; sets: LoggedSet[] }>
  putWorkout: (id: string, body: PutWorkoutBody) => Promise<Workout>
  deleteWorkout: (id: string) => Promise<void>
  /** URL a media element can load — the token rides in the query, headers being unavailable. */
  mediaUrl: (setId: string, slot: MediaSlot) => string
}

interface RealApiOptions {
  readonly client: ApiClient
  readonly baseUrl: string
  readonly getToken: () => string | null
}

function json(body: unknown): RequestInit {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }
}

/**
 * A write of a row the account owns. It says when it was made, to the millisecond: the server
 * refuses one older than the row it would overwrite, so a device holding a stale answer cannot
 * quietly undo a change made somewhere else.
 */
function write(body: object): RequestInit {
  return json({ ...body, updatedAt: updatedAtOf(Date.now()) })
}

/** A delete says when it was made the only way a body-less request can: in the query. */
function gone(path: string): string {
  const updatedAt = encodeURIComponent(updatedAtOf(Date.now()))
  return `${path}${path.includes('?') ? '&' : '?'}updatedAt=${updatedAt}`
}

export function createApi({ client, baseUrl, getToken }: RealApiOptions): Api {
  return {
    sync: ({ since, limit }) =>
      client.request<SyncPage>(
        `/sync?since=${String(since)}${limit === undefined ? '' : `&limit=${String(limit)}`}`,
      ),
    exportData: (groups, format) =>
      client.download(`/export?groups=${groups.join(',')}&format=${format}`),
    importData: (body) => client.request<ImportWritten>('/imports', json(body)),
    signInWithGoogle: (idToken) =>
      client.request<{ token: string; user: User }>('/auth/google', json({ idToken })),
    signOut: async () => {
      await client.request('/auth/signout', { method: 'POST' })
    },
    getMe: async () => (await client.request<{ user: User }>('/me')).user,
    updateUnits: async (units) =>
      (await client.request<{ user: User }>('/me', { ...json({ units }), method: 'PATCH' })).user,
    updateDistanceUnits: async (distanceUnits) =>
      (
        await client.request<{ user: User }>('/me', {
          ...json({ distanceUnits }),
          method: 'PATCH',
        })
      ).user,
    updateSettings: async (settings) =>
      (await client.request<{ user: User }>('/me', { ...json({ settings }), method: 'PATCH' }))
        .user,
    deleteAccount: async () => {
      await client.request('/me', { method: 'DELETE' })
    },

    sendFeedback: async (body) =>
      (
        await client.request<{ feedback: Feedback }>(
          '/feedback',
          json({ body, context: appContext() }),
        )
      ).feedback,
    listFeedback: async () =>
      (await client.request<{ feedback: Feedback[] }>('/feedback')).feedback,
    setFeedbackStatus: async (id, status) =>
      (
        await client.request<{ feedback: Feedback }>(`/feedback/${id}`, {
          ...json({ status }),
          method: 'PATCH',
        })
      ).feedback,

    putTemplate: async (id, body) =>
      (
        await client.request<{ template: WorkoutTemplate }>(`/templates/${id}`, {
          ...write(body),
          method: 'PUT',
        })
      ).template,
    deleteTemplate: async (id) => {
      await client.request(gone(`/templates/${id}`), { method: 'DELETE' })
    },
    publishTemplate: async (id) =>
      (await client.request<{ listing: LibraryListing }>(`/templates/${id}/publish`, json({})))
        .listing,
    unpublishTemplate: async (id) => {
      await client.request(`/templates/${id}/publish`, { method: 'DELETE' })
    },
    getTemplateVersion: async (id) =>
      (await client.request<{ version: TemplateVersion }>(`/templates/versions/${id}`)).version,
    enroll: async (body) =>
      (await client.request<{ enrollment: Enrollment }>('/templates/enrollments', write(body)))
        .enrollment,
    moveEnrollmentToVersion: async (id, versionId) =>
      (
        await client.request<{ enrollment: Enrollment }>(
          `/templates/enrollments/${id}/version`,
          write({ versionId }),
        )
      ).enrollment,
    setEnrollmentAnchor: async (id, anchorDay) =>
      (
        await client.request<{ enrollment: Enrollment }>(
          `/templates/enrollments/${id}/anchor`,
          write({ anchorDay }),
        )
      ).enrollment,
    endEnrollment: async (id) => {
      await client.request(`/templates/enrollments/${id}/end`, { method: 'POST' })
    },
    deleteEnrollment: async (id) => {
      await client.request(gone(`/templates/enrollments/${id}`), { method: 'DELETE' })
    },

    explore: async (query) => {
      const params = new URLSearchParams()
      if (query.search !== undefined && query.search.length > 0) params.set('search', query.search)
      if (query.schedule !== undefined) params.set('schedule', query.schedule)
      if (query.workouts !== undefined) params.set('workouts', String(query.workouts))
      if (query.exercise !== undefined && query.exercise.length > 0) {
        params.set('exercise', query.exercise)
      }
      if (query.sort !== undefined) params.set('sort', query.sort)
      const suffix = params.size > 0 ? `?${params.toString()}` : ''
      return (await client.request<{ listings: LibraryListing[] }>(`/explore${suffix}`)).listings
    },
    exploreTemplate: async (templateId) =>
      (await client.request<{ listing: LibraryTemplate }>(`/explore/${templateId}`)).listing,

    listExercises: async () =>
      (await client.request<{ exercises: Exercise[] }>('/exercises')).exercises,

    putSet: async (id, body) =>
      (await client.request<{ set: LoggedSet }>(`/sets/${id}`, { ...write(body), method: 'PUT' }))
        .set,
    deleteSet: async (id) => {
      await client.request(gone(`/sets/${id}`), { method: 'DELETE' })
    },
    getSet: async (id) => (await client.request<{ set: LoggedSet }>(`/sets/${id}`)).set,
    getWorkout: (id) =>
      client.request<{ workout: WorkoutRow; sets: LoggedSet[] }>(`/workouts/${id}`),
    putWorkout: async (id, body) =>
      (
        await client.request<{ workout: Workout }>(`/workouts/${id}`, {
          ...write(body),
          method: 'PUT',
        })
      ).workout,
    deleteWorkout: async (id) => {
      await client.request(gone(`/workouts/${id}`), { method: 'DELETE' })
    },

    mediaUrl: (setId, slot) => {
      const token = getToken() ?? ''
      return `${baseUrl}/sets/${setId}/${slot}?token=${encodeURIComponent(token)}`
    },

    putWeighIn: async (day, body) =>
      (
        await client.request<{ weighIn: WeighIn }>(`/weighins/${day}`, {
          ...write(body),
          method: 'PUT',
        })
      ).weighIn,
    putWeighInPhoto: async (day, id, type, label) =>
      (
        await client.request<{ photo: WeighInPhoto }>(`/weighins/${day}/photos/${id}`, {
          ...write({ type, label }),
          method: 'PUT',
        })
      ).photo,
    uploadWeighInPhoto: async (id, media) =>
      (
        await client.request<{ photo: WeighInPhoto }>(`/weighins/photos/${id}/media`, {
          method: 'PUT',
          headers: { 'Content-Type': media.type === '' ? 'application/octet-stream' : media.type },
          body: media,
        })
      ).photo,
    deleteWeighInPhoto: async (id) => {
      await client.request(gone(`/weighins/photos/${id}`), { method: 'DELETE' })
    },
    weighInPhotoUrl: (id) => {
      const token = getToken() ?? ''
      return `${baseUrl}/weighins/photos/${id}/media?token=${encodeURIComponent(token)}`
    },

    putEntry: async (id, body) =>
      (
        await client.request<{ entry: Entry }>(`/nutrition/entries/${id}`, {
          ...write(body),
          method: 'PUT',
        })
      ).entry,
    deleteEntry: async (id) => {
      await client.request(gone(`/nutrition/entries/${id}`), { method: 'DELETE' })
    },
    deleteEntries: async (ids) => {
      await client.request('/nutrition/entries/delete', write({ ids }))
    },
    setDayIncomplete: async (day, incomplete) => {
      await client.request(`/nutrition/days/${day}`, { ...write({ incomplete }), method: 'PUT' })
    },
    putGoal: async (body) =>
      (await client.request<{ goal: Goal }>('/nutrition/goal', { ...write(body), method: 'PUT' }))
        .goal,
    addGoalTargets: async (body) =>
      (await client.request<{ goal: Goal }>('/nutrition/goal/targets', write(body))).goal,
    changeCheckinWeekday: async (weeklyCheckinWeekday) =>
      (
        await client.request<{ goal: Goal }>(
          '/nutrition/goal/weekday',
          write({ weeklyCheckinWeekday }),
        )
      ).goal,
    weeklyCheckedIn: async (day) => {
      await client.request('/nutrition/goal/weekly-checkin', write({ day }))
    },
    endGoal: async (day) =>
      (await client.request<{ goal: Goal }>('/nutrition/goal/end', write({ day }))).goal,
    reopenGoal: async () =>
      (await client.request<{ goal: Goal }>('/nutrition/goal/reopen', write({}))).goal,
    deleteGoal: async () => {
      await client.request(gone('/nutrition/goal'), { method: 'DELETE' })
    },
    askAi: (body) => client.request<AiAnswer>('/nutrition/ai', json(body)),
    readLabel: async (photos) =>
      (await client.request<{ label: AiLabel }>('/nutrition/ai/label', json(photos))).label,
    searchFoods: async (query, from) => {
      // Narrowed, it names what is on — `mine` and pack ids — see `routes/foods.ts`.
      const narrowed = from == null ? '' : `&from=${encodeURIComponent(from.join(','))}`
      const answer = await client.request<{ foods: FoundFood[]; libraryUnavailable?: boolean }>(
        `/foods?q=${encodeURIComponent(query)}${narrowed}`,
      )
      return { foods: answer.foods, libraryUnavailable: answer.libraryUnavailable === true }
    },
    packs: async () => (await client.request<{ packs: PackListed[] }>('/packs')).packs,
    addFavourite: async (key, food) => {
      await client.request(`/foods/favourites/${encodeURIComponent(key)}`, {
        ...write({ food }),
        method: 'PUT',
      })
    },
    removeFavourite: async (key) => {
      await client.request(gone(`/foods/favourites/${encodeURIComponent(key)}`), {
        method: 'DELETE',
      })
    },
    foodByBarcode: async (code) => {
      try {
        return (await client.request<{ food: Food }>(`/foods/barcode/${encodeURIComponent(code)}`))
          .food
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
          return null
        }
        throw error
      }
    },
    foodsByRef: async (refs) =>
      refs.length === 0
        ? []
        : (
            await client.request<{ foods: (Food | null)[] }>(
              `/foods/refs?refs=${encodeURIComponent(refs.join(','))}`,
            )
          ).foods,
    putFood: async (id, body) =>
      (await client.request<{ food: Food }>(`/foods/${id}`, { ...write(body), method: 'PUT' }))
        .food,
    deleteFood: async (id) => {
      await client.request(gone(`/foods/${id}`), { method: 'DELETE' })
    },
    putMeal: async (id, body) =>
      (await client.request<{ meal: Meal }>(`/meals/${id}`, { ...write(body), method: 'PUT' }))
        .meal,
    deleteMeal: async (id) => {
      await client.request(gone(`/meals/${id}`), { method: 'DELETE' })
    },
    logMeal: async (mealId, body) =>
      (await client.request<{ meal: Meal }>(`/meals/${mealId}/logs`, write(body))).meal,
    putUnkeptLog: async (id, body) =>
      (
        await client.request<{ log: MealLog }>(`/meals/logs/${encodeURIComponent(id)}`, {
          ...write(body),
          method: 'PUT',
        })
      ).log,
    addMealFavourite: async (id, meal) => {
      await client.request(`/meals/favourites/${encodeURIComponent(id)}`, {
        ...write({
          meal: { name: meal.name, emoji: null, slots: meal.slots, state: meal.state },
        }),
        method: 'PUT',
      })
    },
    removeMealFavourite: async (id) => {
      await client.request(gone(`/meals/favourites/${encodeURIComponent(id)}`), {
        method: 'DELETE',
      })
    },
  }
}
